import { useState, useEffect, useCallback, useRef } from 'react'
import { useRequestUserConfigContext } from './request'
import availabilityClient from '../clients/availability-client'
import {
    FetchArrivalDatesByMonthRequest,
    FetchDepartureDatesByMonthRequest,
} from '../transports/availability-transport'
import { SearchbarFieldsReturn } from '../components/property-details/hooks/use-searchbar'

import format from 'date-fns/format'

const AVAILABILITY_DURATION = 23

export const useAvailabilityForDates = () => {
    const user = useRequestUserConfigContext()
    const [availableDates, setAvailableDates] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const abortControllerRef = useRef<AbortController | null>(null)

    const fetchDates = useCallback(
        async (
            params: FetchArrivalDatesByMonthRequest | FetchDepartureDatesByMonthRequest,
            type: 'arrival' | 'departure',
        ) => {
            if (isLoading) {
                return
            }

            // Cancel previous request if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
            abortControllerRef.current = new AbortController()

            setIsLoading(true)
            setError(null)

            try {
                const response =
                    type === 'arrival'
                        ? await availabilityClient.getAvailableArrivalDates({
                              user,
                              params,
                              signal: abortControllerRef.current.signal,
                          })
                        : await availabilityClient.getAvailableDepartureDates({
                              user,
                              params,
                              signal: abortControllerRef.current.signal,
                          })

                setAvailableDates(response?.data?.dates || [])
            } catch (error_) {
                if (error_ instanceof Error && error_.name !== 'AbortError') {
                    setError(error_ as Error)
                }
            } finally {
                setIsLoading(false)
            }
        },
        [user], // Removed isLoading from dependencies
    )

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [])

    return {
        availableDates,
        isLoading,
        error,
        fetchDates,
    }
}

interface UseDynamicAvailabilityProps {
    searchbarData: SearchbarFieldsReturn
    listingId: string
}

export const useDynamicAvailability = ({ searchbarData, listingId }: UseDynamicAvailabilityProps) => {
    const [arrivalBasedAvailableDates, setArrivalBasedAvailableDates] = useState<Date[] | null>(null)
    const [departureBasedAvailableDates, setDepartureBasedAvailableDates] = useState<Date[] | null>(null)
    const lastFetchedArrivalDate = useRef<string | null>(null)

    const {
        availableDates: arrivalAvailableDates,
        isLoading: isArrivalLoading,
        error: arrivalError,
        fetchDates: fetchArrivalDates,
    } = useAvailabilityForDates()

    const {
        availableDates: departureAvailableDates,
        isLoading: isDepartureLoading,
        error: departureError,
        fetchDates: fetchDepartureDates,
    } = useAvailabilityForDates()

    const convertToDateArray = useCallback((dates: string[]): Date[] => dates.map(dateStr => new Date(dateStr)), [])

    useEffect(() => {
        const currentMonth = format(
            new Date(
                Math.min(searchbarData?.arrival ? new Date(searchbarData.arrival).getTime() : Date.now(), Date.now()),
            ),
            'yyyy-MM',
        )

        const currentArrivalDate = searchbarData?.arrival ? format(searchbarData.arrival, 'yyyy-MM-dd') : null

        const shouldFetchArrivalDates = arrivalBasedAvailableDates === null

        const shouldFetchDepartureDates =
            searchbarData?.arrival && currentArrivalDate !== lastFetchedArrivalDate.current

        if (!shouldFetchArrivalDates && !shouldFetchDepartureDates) {
            return
        }

        const fetchData = async () => {
            if (shouldFetchArrivalDates) {
                const arrivalParams = {
                    month: currentMonth,
                    duration: AVAILABILITY_DURATION,
                    listingId,
                }

                await fetchArrivalDates(arrivalParams, 'arrival')
            }

            if (shouldFetchDepartureDates) {
                const departureParams = {
                    month: currentMonth,
                    arrivalDate: currentArrivalDate!,
                    duration: AVAILABILITY_DURATION,
                    listingId,
                }

                await fetchDepartureDates(departureParams, 'departure')
                lastFetchedArrivalDate.current = currentArrivalDate
            }
        }

        fetchData()
    }, [searchbarData?.arrival, listingId, fetchArrivalDates, fetchDepartureDates, arrivalBasedAvailableDates])

    useEffect(() => {
        if (arrivalAvailableDates.length > 0) {
            setArrivalBasedAvailableDates(convertToDateArray(arrivalAvailableDates))
        }
    }, [arrivalAvailableDates, convertToDateArray])

    useEffect(() => {
        if (departureAvailableDates.length > 0) {
            setDepartureBasedAvailableDates(convertToDateArray(departureAvailableDates))
        }
    }, [departureAvailableDates, convertToDateArray])

    const activeAvailableDates =
        searchbarData?.arrival && !searchbarData?.departure ? departureBasedAvailableDates : arrivalBasedAvailableDates

    return {
        departureBasedAvailableDates,
        arrivalBasedAvailableDates,
        availableDates: activeAvailableDates,
        isLoading: isArrivalLoading || isDepartureLoading,
        error: arrivalError || departureError,
    }
}
